import React, { useEffect, useState } from 'react';
import { Loader, Trophy } from 'lucide-react';
import API from '../../../api/API';
import { LinkSquadra } from '../../../components/statistiche/Link';
import { calcolaStatisticheSquadra } from './util';
import { slugDaId } from './squadre';

/**
 * Classifica generale dell'edizione: ordine fornito dal backend
 * (per fase raggiunta + fascia), statistiche squadra unite dal frontend.
 */
const ClassificaGenerale = ({ edizioneId, statistiche }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annullato = false;
    if (!edizioneId) {
      setItems(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    // await invece di .then: la classifica è un riquadro dentro la pagina dell'edizione, e una
    // risposta inattesa deve restare confinata qui invece di far cadere tutta la pagina.
    (async () => {
      try {
        const res = await API.planning.classificaGenerale(edizioneId);
        if (annullato) return;
        if (res.isSuccess) setItems(res.data?.items || []);
        else setError(res.errorMsg || 'Errore nel caricamento della classifica');
      } catch (err) {
        if (!annullato) setError(err.message || 'Errore nel caricamento della classifica');
      } finally {
        if (!annullato) setLoading(false);
      }
    })();
    return () => {
      annullato = true;
    };
  }, [edizioneId]);

  // Statistiche squadra indicizzate per id, calcolate una sola volta. Non per nome: è la stessa
  // chiave con cui il link della riga risolve lo slug qualche riga più sotto, e due squadre della
  // stessa edizione possono chiamarsi uguale.
  const statsById = React.useMemo(() => {
    const map = {};
    (statistiche?.squadre || []).forEach((s) => {
      map[s.idSquadra] = calcolaStatisticheSquadra(s, statistiche.giocatori, statistiche.partite);
    });
    return map;
  }, [statistiche]);

  const rows = (items || []).map((it) => ({
    ...it,
    stats: statsById[it.squadraId] || null,
  }));

  const podiumClass = (pos) => (pos === 1 ? 'pos-1' : pos === 2 ? 'pos-2' : pos === 3 ? 'pos-3' : '');

  if (loading) {
    return (
      <div className="cg-loading">
        <Loader className="cg-loading-icon" />
        <span>Calcolo classifica…</span>
      </div>
    );
  }

  if (error) {
    return <div className="cg-empty">{error}</div>;
  }

  if (!items || items.length === 0) {
    return (
      <div className="cg-empty">
        <Trophy className="cg-empty-icon" />
        <p>Classifica non ancora disponibile</p>
      </div>
    );
  }

  return (
    <div className="cg">
      {/* Desktop: tabella */}
      <div className="cg-table-wrap">
        <table className="cg-table">
          <thead>
            <tr>
              <th className="cg-pos">#</th>
              <th className="cg-team">Squadra</th>
              <th className="cg-phase">Fase raggiunta</th>
              <th>Punti</th>
              <th>Mete</th>
              <th>Pieni</th>
              <th>Vuoti</th>
              <th>Assist</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.squadraId} className={`cg-row ${podiumClass(r.posizione)}`}>
                <td className="cg-pos">
                  <span className={`cg-badge ${podiumClass(r.posizione)}`}>{r.posizione}</span>
                </td>
                <td className="cg-team"><LinkSquadra slug={slugDaId(statistiche, r.squadraId)} nome={r.nomeSquadra} /></td>
                <td className="cg-phase"><span className="cg-phase-chip">{r.faseLabel}</span></td>
                <td className="cg-num cg-pts">{r.stats ? r.stats.puntiTotali : '—'}</td>
                <td className="cg-num">{r.stats ? r.stats.meteTotali : '—'}</td>
                <td className="cg-num">{r.stats ? r.stats.pieniTotali : '—'}</td>
                <td className="cg-num">{r.stats ? r.stats.vuotiTotali : '—'}</td>
                <td className="cg-num">{r.stats ? r.stats.assistTotali : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: card */}
      <div className="cg-cards">
        {rows.map((r) => (
          <div key={r.squadraId} className={`cg-card ${podiumClass(r.posizione)}`}>
            <div className="cg-card-head">
              <span className={`cg-badge ${podiumClass(r.posizione)}`}>{r.posizione}</span>
              <div className="cg-card-title">
                <span className="cg-card-name"><LinkSquadra slug={slugDaId(statistiche, r.squadraId)} nome={r.nomeSquadra} /></span>
                <span className="cg-phase-chip">{r.faseLabel}</span>
              </div>
              <span className="cg-card-pts">{r.stats ? r.stats.puntiTotali : '—'}<small>pt</small></span>
            </div>
            <div className="cg-card-stats">
              <div><span>{r.stats ? r.stats.meteTotali : '—'}</span><small>Mete</small></div>
              <div><span>{r.stats ? r.stats.pieniTotali : '—'}</span><small>Pieni</small></div>
              <div><span>{r.stats ? r.stats.vuotiTotali : '—'}</span><small>Vuoti</small></div>
              <div><span>{r.stats ? r.stats.assistTotali : '—'}</span><small>Assist</small></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClassificaGenerale;
